import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  PlusIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../@/components/ui/tabs";
import { useToast } from "../../@/components/ui/use-toast";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";
import SortupDialog from "../../components/common/SortupDialog";

interface TermsSection {
  title: string;
  paragraphs: string[];
}

const EditTermsAndconditions = () => {
  // State for storing all terms sections
  const [termsSections, setTermsSections] = useState<
    Record<string, TermsSection>
  >({});

  // State for tracking which section is being edited
  const [editingSection, setEditingSection] = useState<string | null>(null);

  // State for tracking which paragraph is being edited
  const [editingParagraphIndex, setEditingParagraphIndex] = useState<
    number | null
  >(null);

  // State for adding new paragraph
  const [isAddingNew, setIsAddingNew] = useState(false);

  // Temporary state for editing
  const [editText, setEditText] = useState("");
  const [editTitle, setEditTitle] = useState("");

  // Current section being edited
  const [currentSection, setCurrentSection] = useState<string>("definitions");

  // Loading state
  const [loading, setLoading] = useState(true);

  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [paragraphToDelete, setParagraphToDelete] = useState<{
    section: string;
    index: number;
  } | null>(null);

  // Section keys mapping
  const sectionKeys = {
    definitions: "Definitions",
    generalSupplemental: "General Supplemental Terms",
    specificSupplemental: "Specific Supplemental Terms",
    marketplace: "Marketplace Method Terms",
    merchantManaged: "Merchant Managed Delivery Method Terms",
    nonDelivery: "Non-Delivery Terms",
    dineIn: "Dine-In Terms",
    webshop: "Webshop Online Ordering Terms",
    sponsoredListing: "Sponsored Listing Terms",
    dataProcessing: "Addendum A: Data Processing Agreement",
    googleCalendar: "Google Calendar Integration Terms",
  };

  // Fetch data from Firebase on component mount
  useEffect(() => {
    const fetchTermsData = async () => {
      try {
        const termsRef = ref(db, "LayoutSettingData/TermsOfService");
        const snapshot = await get(termsRef);

        if (snapshot.exists()) {
          setTermsSections(snapshot.val());
        } else {
          // Initialize with default content from existing components
          const initialData: Record<string, TermsSection> = {
            definitions: {
              title: "Definitions",
              paragraphs: [
                "The following terms have the respective meanings given to them below:",
                "\"Activation Fee\" means a Fee paid by Merchant to Uber Canada in installments until the full amount is paid in consideration of Uber's work to activate Merchant on the Uber Eats App. Merchant's Activation Fee is specifically set forth in the Order Form.",
                '"Administrative Fee" means collectively, the Activation Fee and the Subscription Fee.',
                '"ADRIC" means the ADR Institute of Canada, Inc.',
                '"ADRIC Rules" means the Arbitration Rules of the ADR Institute of Canada, Inc.',
                '"Ads Manager" means Uber or its affiliates\' proprietary, automated, self-service sponsored listings tools',
                '"Affiliate" means an entity that owns or controls, is owned or controlled by, or is or under common control or ownership of a party, where control is defined as the possession, directly or indirectly, of the power to direct or cause the direction of the management and policies of an entity, whether through ownership of voting securities, by contract or otherwise. Both Uber B.V. and Uber Payments B.V. are Affiliates of both UTI and Uber Canada.',
                '"Agreement" means the Order Form, the Definitions and the Supplemental Terms.',
                '"Aggregate Data" means aggregate, deidentified data related to Merchant\'s transactions in connection with the Uber Eats Services.',
                '"Applicable Law" shall mean all applicable laws, statutes, regulations, government codes and government policies from time to time in force (including without limitation all applicable data protection and privacy laws).',
                '"Batched Trip" means an instance where one Marketplace Delivery Person may deliver orders to multiple Customers as part of the same trip, including multiple orders from one Merchant, or including one or more orders from one Merchant combined with one or more orders from other Merchants using the Uber Eats App or other products or services provided by Uber or its Affiliates.',
                '"Brand Matter" means an event that, in Uber\'s reasonable judgment, would jeopardize the ability of the Merchant to comply with or perform this Agreement, or prejudice the goodwill or reputation of Uber or its Affiliates, and that may include alleged violations of the Community Guidelines or any Applicable Laws.',
                '"Bid" means an offer for a Sponsored Listing on the Uber Eats App',
                '"Campaign" means a sponsored listing campaign that includes a recurring budget amount, Bid method (automatic or custom), Bid amount (if applicable), and the relevant Merchant locations (if applicable)',
                "\"Co-Funded Offer\" means Uber's agreement to fund a portion of Merchant's Offer pursuant to the terms of a written Promotion Schedule.",
                '"Confidential Information" means any confidential, proprietary or other non-public information disclosed by or on behalf of one party (the "Discloser") to another (the "Recipient"), whether disclosed verbally, in writing, or by inspection of tangible objects, and includes, without limitation, transactional, operational, performance and other data or information (including, Uber Data, Customers\' information, package information, and the transaction volume, marketing and business plans, business, financial, technical, operational) and/or that is related to the sale of Merchant\'s Items to Customers through the Uber Eats App and the terms and conditions of this Agreement.',
              ],
            },
            generalSupplemental: {
              title: "General Supplemental Terms",
              paragraphs: [
                "The General Supplemental Terms apply to all merchants using the Uber Eats App and govern the conditions and agreements between Uber and merchants.",
                '"Activation Period" refers to the duration between the date of agreement and the date of actual activation of the merchant\'s account.',
                '"Service Fee" means the percentage fee charged on each transaction processed through the Uber Eats App.',
                '"Territory" refers to the geographical area where the merchant operates and offers services via Uber Eats.',
                "These terms supplement the primary agreement and provide additional details regarding usage of the Uber Eats platform.",
                'The "Merchant Agreement" is the overall contract between Uber and the merchant, inclusive of all supplemental terms and conditions.',
                "Merchants are responsible for compliance with all applicable laws, including but not limited to food safety and consumer protection regulations.",
                "Uber reserves the right to modify the terms at any time, provided the merchant is notified in advance.",
              ],
            },
            specificSupplemental: {
              title: "Specific Supplemental Terms",
              paragraphs: [
                "These Specific Supplemental Terms address the unique conditions applicable to different merchant types or service offerings.",
                '"Marketplace Services" refer to services provided by merchants directly to customers via the Uber Eats platform.',
                "Additional fees may apply based on the specific services provided by the merchant, including but not limited to premium delivery options.",
                "These terms are designed to ensure fair practices across various merchant types and service categories.",
                'The "Compliance Clause" states that all merchants must adhere to their local jurisdiction\'s regulations for food handling and delivery services.',
              ],
            },
            marketplace: {
              title: "Marketplace Method Terms",
              paragraphs: [
                "These terms outline the rules and conditions for the marketplace method of delivery on the Uber Eats platform.",
                '"Delivery Partner" refers to the independent contractor responsible for fulfilling deliveries on behalf of the merchant.',
                "Merchants are charged a flat-rate service fee for each order completed through the marketplace method.",
                "Uber's marketplace method allows multiple orders to be bundled and delivered by one delivery partner.",
                'The "Bundled Delivery Clause" enables more efficient use of delivery resources while maintaining customer satisfaction.',
              ],
            },
            merchantManaged: {
              title: "Merchant Managed Delivery Method Terms",
              paragraphs: [
                "These terms apply to merchants who manage their own delivery services while using the Uber Eats platform for order processing.",
                "Merchants are responsible for hiring and managing their own delivery personnel.",
                'The "Merchant Delivery Fee" is set by the merchant and may differ from Uber\'s standard delivery fees.',
                "Merchants must maintain appropriate insurance coverage for their delivery operations.",
              ],
            },
            nonDelivery: {
              title: "Non-Delivery Terms",
              paragraphs: [
                "These terms apply to merchants who provide services that do not involve delivery, such as pick-up or in-house dining.",
                "Merchants must ensure that accurate information is provided for customers who choose the non-delivery option.",
                "Fees and payment processing are managed through the Uber Eats platform, even when delivery is not required.",
              ],
            },
            dineIn: {
              title: "Dine-In Terms",
              paragraphs: [
                "These terms cover merchants offering dine-in services through Uber Eats.",
                "Customers can reserve tables or pre-order food for in-house dining using the app.",
                "Merchants are responsible for managing reservations and ensuring the timely preparation of food.",
              ],
            },
            webshop: {
              title: "Webshop Online Ordering Terms",
              paragraphs: [
                "These terms apply to merchants who utilize the Uber Eats webshop for online ordering.",
                'The "Webshop Fee" refers to the fee charged for maintaining the merchant\'s online storefront.',
                "Merchants must keep their menu and pricing information up-to-date on the webshop platform.",
              ],
            },
            sponsoredListing: {
              title: "Sponsored Listing Terms",
              paragraphs: [
                "Sponsored listings allow merchants to promote their businesses on the Uber Eats app through paid advertising.",
                "Merchants can create custom campaigns, set budgets, and bid on sponsored placements within the Uber Eats app.",
              ],
            },
            dataProcessing: {
              title: "Addendum A: Data Processing Agreement",
              paragraphs: [
                "This addendum outlines the terms for processing customer data in compliance with applicable privacy laws.",
                "Merchants must handle customer data in accordance with data protection regulations.",
                "Uber and merchants share responsibility for safeguarding customer information collected through the platform.",
              ],
            },
            googleCalendar: {
              title: "Google Calendar Integration Terms",
              paragraphs: [
                "These terms govern the use of Afrocart's integration with Google Calendar, allowing users to manage their events seamlessly through our platform.",
                "By connecting your Google Calendar to Afrocart, you agree to grant the necessary permissions for adding, editing, retrieving, and updating events within your calendar.",
                "Afrocart uses these permissions solely to provide calendar management features as authorized by you. Your access tokens and refresh tokens are securely stored using encryption and are not shared with third parties.",
                "For further details on how we handle your data, please review our Privacy Policy.",
              ],
            },
          };

          setTermsSections(initialData);
          await saveToFirebase(initialData);
        }
      } catch (error) {
        console.error("Error fetching terms data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTermsData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data: Record<string, TermsSection>) => {
    try {
      const termsRef = ref(db, "LayoutSettingData/TermsOfService");
      await set(termsRef, data);
      return true;
    } catch (error) {
      console.error("Error saving terms data:", error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
        showIcon: true,
      });
      return false;
    }
  };

  // Handle editing section title
  const handleEditSectionTitle = (sectionKey: string) => {
    setEditingSection(sectionKey);
    setEditTitle(termsSections[sectionKey].title);
  };

  // Handle saving section title
  const handleSaveSectionTitle = async () => {
    if (editingSection) {
      const updatedSections = {
        ...termsSections,
        [editingSection]: {
          ...termsSections[editingSection],
          title: editTitle,
        },
      };
      const success = await saveToFirebase(updatedSections);
      if (success) {
        setTermsSections(updatedSections);
        setEditingSection(null);
        toast({
          title: "Success",
          description: "Section title updated successfully",
          variant: "success",
          showIcon: true,
        });
      }
    }
  };

  // Handle editing paragraph
  const handleEditParagraph = (index: number) => {
    setEditingParagraphIndex(index);
    setEditText(termsSections[currentSection].paragraphs[index]);
  };

  // Handle saving paragraph
  const handleSaveParagraph = async () => {
    if (editingParagraphIndex !== null) {
      if (!editText.trim()) {
        toast({
          title: "Error",
          description: "Paragraph content cannot be empty",
          variant: "destructive",
          showIcon: true,
        });
        return;
      }

      const updatedParagraphs = [...termsSections[currentSection].paragraphs];
      updatedParagraphs[editingParagraphIndex] = editText;

      const updatedSections = {
        ...termsSections,
        [currentSection]: {
          ...termsSections[currentSection],
          paragraphs: updatedParagraphs,
        },
      };

      const success = await saveToFirebase(updatedSections);
      if (success) {
        setTermsSections(updatedSections);
        setEditingParagraphIndex(null);
        toast({
          title: "Success",
          description: "Paragraph updated successfully",
          variant: "success",
          showIcon: true,
        });
      }
    }
  };

  // Handle deleting paragraph
  const handleDeleteParagraph = (section: string, index: number) => {
    setParagraphToDelete({ section, index });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!paragraphToDelete) return;

    const { section, index } = paragraphToDelete;
    const updatedParagraphs = [...termsSections[section].paragraphs];
    updatedParagraphs.splice(index, 1);

    const updatedSections = {
      ...termsSections,
      [section]: {
        ...termsSections[section],
        paragraphs: updatedParagraphs,
      },
    };

    const success = await saveToFirebase(updatedSections);
    if (success) {
      setTermsSections(updatedSections);
      toast({
        title: "Success",
        description: "Paragraph deleted successfully",
        variant: "success",
        showIcon: true,
      });
    }
    setShowDeleteDialog(false);
    setParagraphToDelete(null);
  };

  // Handle adding new paragraph
  const handleAddNew = () => {
    setIsAddingNew(true);
    setEditText("");
  };

  // Handle saving new paragraph
  const handleSaveNew = async () => {
    if (editText.trim() !== "") {
      const updatedParagraphs = [
        ...termsSections[currentSection].paragraphs,
        editText,
      ];

      const updatedSections = {
        ...termsSections,
        [currentSection]: {
          ...termsSections[currentSection],
          paragraphs: updatedParagraphs,
        },
      };

      const success = await saveToFirebase(updatedSections);
      if (success) {
        setTermsSections(updatedSections);
        setIsAddingNew(false);
        setEditText("");
        toast({
          title: "Success",
          description: "New paragraph added successfully",
          variant: "success",
          showIcon: true,
        });
      }
    }
  };

  // Handle canceling edit
  const handleCancel = () => {
    setEditingSection(null);
    setEditingParagraphIndex(null);
    setIsAddingNew(false);
    setEditText("");
    setEditTitle("");
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Manage Terms & Conditions
          </h1>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Section Navigation - Scrollable on mobile */}
          <div className="md:w-1/4">
            <div className="w-full overflow-x-auto md:overflow-visible pb-2 mb-2 md:mb-0">
              <div className="flex md:flex-col min-w-fit md:min-w-0 bg-gray-50 p-4 rounded-lg space-x-4 md:space-x-0 md:space-y-2">
                {Object.entries(sectionKeys).map(([key, title]) => (
                  <div
                    key={key}
                    className={`cursor-pointer md:text-wrap p-2 rounded whitespace-nowrap ${
                      currentSection === key
                        ? "bg-customGreenColor text-white"
                        : "hover:bg-gray-100"
                    }`}
                    onClick={() => setCurrentSection(key)}
                  >
                    {title}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Section Content */}
          <div className="md:w-3/4">
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              {/* Section Title */}
              <div className="mb-6 flex justify-between items-center">
                {editingSection === currentSection ? (
                  <div className="w-full">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-xl font-bold"
                    />
                    <div className="flex space-x-2 mt-2">
                      <button
                        onClick={handleSaveSectionTitle}
                        className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" /> Save
                      </button>
                      <button
                        onClick={handleCancel}
                        className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
                      >
                        <XIcon className="h-4 w-4 mr-1" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h2 className="text-2xl font-bold">
                      {termsSections[currentSection]?.title}
                    </h2>
                    <button
                      className="text-customGreenColor"
                      onClick={() => handleEditSectionTitle(currentSection)}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                  </>
                )}
              </div>

              {/* Add New Paragraph Button */}
              <div className="mb-4">
                <button
                  className="text-white bg-customGreenColor px-3 py-1 rounded flex items-center"
                  onClick={handleAddNew}
                >
                  <PlusIcon className="h-4 w-4 mr-1" /> Add Paragraph
                </button>
              </div>

              {/* Add New Paragraph Form */}
              {isAddingNew && (
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <h3 className="font-helveticaBold text-black text-lg mb-3">
                    Add New Paragraph
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <textarea
                        value={editText}
                        onChange={(e) => setEditText(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded min-h-[150px]"
                        placeholder="Enter paragraph content"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveNew}
                        className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" /> Save
                      </button>
                      <button
                        onClick={handleCancel}
                        className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
                      >
                        <XIcon className="h-4 w-4 mr-1" /> Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Paragraphs List */}
              <div className="space-y-4">
                {termsSections[currentSection]?.paragraphs.map(
                  (paragraph, index) => (
                    <div key={index} className="border-b border-gray-200 pb-4">
                      {editingParagraphIndex === index ? (
                        <div>
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded min-h-[150px]"
                          />
                          <div className="flex space-x-2 mt-2">
                            <button
                              onClick={handleSaveParagraph}
                              className="bg-customGreenColor text-white px-3 py-1 rounded flex items-center"
                            >
                              <CheckIcon className="h-4 w-4 mr-1" /> Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="bg-gray-300 text-gray-700 px-3 py-1 rounded flex items-center"
                            >
                              <XIcon className="h-4 w-4 mr-1" /> Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex justify-between items-start">
                            <p className="text-gray-700 whitespace-pre-line">
                              {paragraph}
                            </p>
                            <div className="flex space-x-2 ml-2">
                              <button
                                className="text-customGreenColor"
                                onClick={() => handleEditParagraph(index)}
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                className="text-red-500"
                                onClick={() =>
                                  handleDeleteParagraph(currentSection, index)
                                }
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setParagraphToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setParagraphToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this paragraph? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditTermsAndconditions;
